import React, { useMemo, useEffect, useState, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { 
  MdSearch,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdInfo
} from 'react-icons/md';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { CustomerService } from '../../Common/Services/CustomerService';
import { DashboardServices } from '../../Common/Services/DashboardServices';
import { VendorService } from '../../Common/Services/VendorService';
import { InvoiceService } from '../../Common/Services/InvoiceService';
import { TransactionService } from '../../Common/Services/TransactionService';
import { JobTrackerServices } from '../../Common/Services/JobTrackerServices';
import { JobOrderService } from '../../Common/Services/JobOrderService';
import { ICustomerorder } from '../../Common/Contracts/ICustomerorder';
import { JobServices } from '../../Common/Services/JobServices';
import { WorkstationService } from '../../Common/Services/WorkstationService';
// Heavy slideout components — lazy-loaded so their code is never parsed until first use
const JobCreations = React.lazy(() => import('../JobCreations'));
const Quotationslideout = React.lazy(() => import('../Quotationslideout'));
const InvoiceSlideout = React.lazy(() => import('../Transaction/InvoiceSlideout'));
const Customerorderslideout = React.lazy(() => import('../Customerorderslideout'));
const DepositSlideout = React.lazy(() => import('../Transaction/DepositSlideout'));
const WithdrawalSlideout = React.lazy(() => import('../Transaction/WithdrawalSlideout'));
const Receivableslideout = React.lazy(() => import('../Accounts/Receivableslideout'));
const InvoiceEntryslideout = React.lazy(() => import('../Purchasing/InvoiceEntryslideout'));
import { CommonService } from '../../Common/Services/CommonService';
import { User } from '../../Common/Services/User';
import { Utils } from '../../Common/Utilis';
import moment from 'moment-timezone';
interface SlideoutState {
  open: boolean;
  type: any;
  data: any | null;
}

const AirframeDashboard: React.FC = () => {
  const locationIdFromRedux = useSelector((state: any) => state.LocationReducer.locationId);
  const permissionsFromRedux = useSelector((state: any) => state.PagePermissionReducer.permissions);
  const hasRevenuePermission = permissionsFromRedux && permissionsFromRedux['View Revenue/Expense'] ? true : false;
  const hasOrderPermission = permissionsFromRedux && permissionsFromRedux['View Order details'] ? true : false;

  // Get locationId from Redux, fallback to localStorage if Redux hasn't initialized yet
  const locationId = locationIdFromRedux || Number(localStorage.getItem('locationId')) || 0;
  const storage = JSON.parse(localStorage.getItem("storage") || '{}');
  const currentUtcTime = storage === null || !storage ? "America/Chicago" : (storage.currentUtcTime as any);
  // Get current user ID from storage
  const currentUserId = storage?.user_UniqueID || storage?.user_UniqueId || storage?.userUniqueID || 0;
  const [currentTimeTz, setCurrentTimeTz] = useState<string>(
    moment().tz(currentUtcTime).format("MM/DD/YY hh:mm A z")
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimeTz(moment().tz(currentUtcTime).format("MM/DD/YY hh:mm A z"));
    }, 60000); // update every minute
    // set immediately as well in case timezone changes
    setCurrentTimeTz(moment().tz(currentUtcTime).format("MM/DD/YY hh:mm A z"));
    return () => clearInterval(interval);
  }, [currentUtcTime]);
  // State for API data
  const [customerOrdersData, setCustomerOrdersData] = useState<ICustomerorder[]>([]);
  const [quotationData, setQuotationData] = useState<ICustomerorder[]>([]);
  const [vendorInvoiceData, setVendorInvoiceData] = useState<any[]>([]);
  const [jobOrderData, setJobOrderData] = useState<any[]>([]);
  const [jobDetailsMap, setJobDetailsMap] = useState<Record<number, any[]>>({}); // Map of jobid -> jobdetails array
  const [workstationMap, setWorkstationMap] = useState<Record<number, string>>({}); // Map of workstationId -> workstationName
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [dashboardJobData, setDashboardJobData] = useState<any>(null);
  const [customerShippedData, setCustomerShippedData] =  useState<any[]>([]);
  const [commentsData, setCommentsData] = useState<any[]>([]);
  const [allBankData, setAllBankData] = useState<any[]>([]);
  const [taskSearchTerm, setTaskSearchTerm] = useState<string>('');
  const [jobSearchTerm, setJobSearchTerm] = useState<string>('');
  const [userMap, setUserMap] = useState<Record<number, string>>({}); // Map of user ID -> user name
  const [tasks, setTasks] = useState<any[]>([]);
  // Per-section loaders only — dashboard shell stays visible (no full-page blank)
  const [sectionLoading, setSectionLoading] = useState({
    dashboard: true,
    dashboardJobs: true,
    shipped: true,
    quotations: true,
    vendorInvoices: true,
    jobs: true,
    bank: true,
    tasks: true,
    messages: true,
  });
  const setSectionBusy = (key: keyof typeof sectionLoading, busy: boolean) => {
    setSectionLoading((prev) => (prev[key] === busy ? prev : { ...prev, [key]: busy }));
  };
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const [slideout, setSlideout] = useState<SlideoutState>({
    open: false,
    type: "",
    data: null
  });
  const [showAllJobs, setShowAllJobs] = useState(false);

  // Skeleton UI components with shimmer effect matching exact card layouts
  const SkeletonBox: React.FC<{ width?: string; height?: string; borderRadius?: string; style?: React.CSSProperties }> = ({
    width = '100%',
    height = '16px',
    borderRadius = '4px',
    style
  }) => (
    <div
      className="skeleton-box"
      style={{
        width,
        height,
        borderRadius,
        ...style
      }}
    />
  );


  const labelMap = {
  JobOrder: 'Job Order No',
  CustomerOrder: 'Customer Order No',
  CustomerInvoice: 'Customer Invoice No',
  vendorInvoice: 'Vendor Invoice No',
  accountsInvoice: 'Invoice No',
  Quotation: 'Quotation No',
  vendorOrder: 'Vendor Order No'
};
  // Fetch customer orders data
  // useEffect(() => {
  //   const fetchCustomerOrders = async () => {
  //     try {
  //       setLoading(true);
  //       const orders = await CustomerService.GetCustomerOrder();
  //       if (orders) {
  //         setCustomerOrdersData(orders);
  //       }
  //     } catch (err) {
  //       console.error('Error fetching customer orders:', err);
  //       setError('Failed to fetch customer orders');
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchCustomerOrders();
  // }, []);  // Consolidated parallel data fetch with sessionStorage cache & stale-while-revalidate
  useEffect(() => {
    let cancelled = false;
    if (!locationId || locationId <= 0) {
      setSectionLoading({
        dashboard: false,
        dashboardJobs: false,
        shipped: false,
        quotations: false,
        vendorInvoices: false,
        jobs: false,
        bank: false,
        tasks: false,
        messages: false,
      });
      return;
    }

    const cacheKey = `airframe_dashboard_cache_v2_${locationId}`;
    let hasValidCache = false;

    // 1. Hydrate from sessionStorage cache instantly if available
    try {
      const cachedRaw = sessionStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && Date.now() - (cached.timestamp || 0) < 5 * 60 * 1000) {
          hasValidCache = true;
          if (cached.shipped) setCustomerShippedData(cached.shipped);
          if (cached.quotations) setQuotationData(cached.quotations);
          if (cached.vendorInvoices) setVendorInvoiceData(cached.vendorInvoices);
          if (cached.jobs) setJobOrderData(cached.jobs);
          if (cached.comments) setCommentsData(cached.comments);
          if (cached.tasks) setTasks(cached.tasks);
          if (cached.dashboard) setDashboardData(cached.dashboard);
          if (cached.dashboardJobs) setDashboardJobData(cached.dashboardJobs);
          if (cached.bank) setAllBankData(cached.bank);

          setSectionLoading({
            dashboard: false,
            dashboardJobs: false,
            shipped: false,
            quotations: false,
            vendorInvoices: false,
            jobs: false,
            bank: false,
            tasks: false,
            messages: false,
          });
          setIsRefreshing(true);
        }
      }
    } catch (e) {}

    if (!hasValidCache) {
      setIsRefreshing(false);
    }

    const updateCache = (key: string, data: any) => {
      try {
        const existing = JSON.parse(sessionStorage.getItem(cacheKey) || '{}');
        existing[key] = data;
        existing.timestamp = Date.now();
        sessionStorage.setItem(cacheKey, JSON.stringify(existing));
      } catch (e) {}
    };

    // 2. Parallel background fetching
    const storage = JSON.parse(localStorage.getItem("storage") || '{}');
    const tenantID = storage?.tenantID || 0;

    const p1 = CustomerService.GetCustomerShipped()
      .then(res => { if (!cancelled && res) { setCustomerShippedData(res); updateCache('shipped', res); } })
      .catch(err => console.error('Error fetching customer shipped:', err))
      .finally(() => { if (!cancelled) setSectionBusy('shipped', false); });

    const p2 = CustomerService.GetQuotationOrder()
      .then(res => { if (!cancelled && res) { setQuotationData(res); updateCache('quotations', res); } })
      .catch(err => console.error('Error fetching quotations:', err))
      .finally(() => { if (!cancelled) setSectionBusy('quotations', false); });

    const p3 = InvoiceService.GetVendorInvoiceList({ locationId })
      .then(res => { if (!cancelled && res) { setVendorInvoiceData(res); updateCache('vendorInvoices', res); } })
      .catch(err => console.error('Error fetching vendor invoices:', err))
      .finally(() => { if (!cancelled) setSectionBusy('vendorInvoices', false); });

    const p4 = JobOrderService.GetJobOrderList({ locationId })
      .then(res => { if (!cancelled && res) { setJobOrderData(res); updateCache('jobs', res); } })
      .catch(err => console.error('Error fetching job orders:', err))
      .finally(() => { if (!cancelled) setSectionBusy('jobs', false); });

    const p5 = CommonService.GetLatestComments({})
      .then(res => { if (!cancelled && res) { setCommentsData(res); updateCache('comments', res); } })
      .catch(err => console.error('Error fetching comments:', err))
      .finally(() => { if (!cancelled) setSectionBusy('messages', false); });

    const p6 = JobServices.GetJobTasks({})
      .then(res => { if (!cancelled && res) { setTasks(res); updateCache('tasks', res); } })
      .catch(err => console.error('Error fetching tasks:', err))
      .finally(() => { if (!cancelled) setSectionBusy('tasks', false); });

    const p7 = DashboardServices.GetCustomerDashboard({})
      .then(res => { if (!cancelled && res) { setDashboardData(res); updateCache('dashboard', res); } })
      .catch(err => console.error('Error fetching customer dashboard:', err))
      .finally(() => { if (!cancelled) setSectionBusy('dashboard', false); });

    const p8 = JobServices.GetDashboardData({})
      .then(res => { if (!cancelled && res) { setDashboardJobData(res); updateCache('dashboardJobs', res); } })
      .catch(err => console.error('Error fetching job dashboard:', err))
      .finally(() => { if (!cancelled) setSectionBusy('dashboardJobs', false); });

    const p9 = (async () => {
      try {
        const banks = await InvoiceService.GetBankMaster({ tenantid: tenantID, locationId });
        if (cancelled) return;
        if (!banks || banks.length === 0) {
          setAllBankData([]);
          updateCache('bank', []);
          return;
        }
        const bankPromises = banks.map(async (bank: any) => {
          try {
            const reg = await InvoiceService.GetRegisterBank({
              isApproved: 1,
              Bankid: bank.id,
              selectedOption: "Last 90 Days",
              locationId
            });
            return reg || [];
          } catch (err) {
            return [];
          }
        });
        const bankResults = await Promise.all(bankPromises);
        if (cancelled) return;
        const flat = bankResults.flat();
        setAllBankData(flat);
        updateCache('bank', flat);
      } catch (err) {
        console.error('Error fetching bank data:', err);
      } finally {
        if (!cancelled) setSectionBusy('bank', false);
      }
    })();

    Promise.allSettled([p1, p2, p3, p4, p5, p6, p7, p8, p9]).finally(() => {
      if (!cancelled) setIsRefreshing(false);
    });

    return () => {
      cancelled = true;
    };
  }, [locationId]);

  // Deferred fetch for workstations (non-critical)
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const storage = JSON.parse(localStorage.getItem("storage") || '{}');
        const tenantID = storage?.tenantID || storage?.tenantid || 0;
        const workstations = await WorkstationService.GetWorkstations({ tenantid: tenantID });
        if (!cancelled && workstations && Array.isArray(workstations)) {
          const wsMap: Record<number, string> = {};
          workstations.forEach((ws: any) => {
            if (ws.id && ws.workstationName) {
              wsMap[ws.id] = ws.workstationName;
            }
          });
          setWorkstationMap(wsMap);
        }
      } catch (err) {
        console.error('Error fetching workstations:', err);
      }
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  // Deferred fetch for users (non-critical)
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const users = await User.getAllUsers();
        if (!cancelled && users && Array.isArray(users)) {
          const userMapData: Record<number, string> = {};
          users.forEach((user: any) => {
            const userId = user.user_UniqueID || user.id || user.ID || user.userId || 0;
            const userName = `${user.firstName} ${user.lastName}` || user.userName || user.name || user.user_Name || '';
            if (userId > 0 && userName) {
              userMapData[userId] = userName;
            }
          });
          setUserMap(userMapData);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    }, 200);

  }, []);


  const customerPayments = useMemo(() => {
    const mtdFromDashboard = Number(dashboardData?.[0]?.mtd ?? 0);
    const ytdFromDashboard = Number(dashboardData?.[0]?.ytd ?? 0);
    const dueFromDashboard = Number(dashboardData?.[0]?.due ?? 0);

    if (!allBankData.length && !mtdFromDashboard && !ytdFromDashboard) {
      return { today: '$0', thisMonth: '$0', due: '$0' };
    }

    if (!allBankData.length) {
      return {
        today: `$${mtdFromDashboard.toLocaleString()}`,
        thisMonth: `$${ytdFromDashboard.toLocaleString()}`,
        due: `$${dueFromDashboard.toLocaleString()}`,
      };
    }

    return {
      today: `$${mtdFromDashboard.toLocaleString()}`,
      thisMonth: `$${ytdFromDashboard.toLocaleString()}`,
      due: `$${dueFromDashboard.toLocaleString()}`,
    };
  }, [allBankData, dashboardData]);

  // YTD Expenses calculation from all bank accounts
  // ytdExpenses — console.logs removed (one was inside reduce, firing per array element)
  const ytdExpenses = useMemo(() => {
    if (!allBankData.length) return '$0';
    const expenseTransactions = allBankData.filter(transaction => transaction.rowType === 1);
    const totalPayments = expenseTransactions.reduce(
      (sum, transaction) => sum + (transaction.payment || 0),
      0
    );
    return `$${totalPayments.toLocaleString()}`;
  }, [allBankData]);

  // YTD Revenue vs Expense comparison
  const ytdComparison = useMemo(() => {
    let ytdRevenue = 0;
    let ytdExpense = 0;

    if (allBankData.length > 0) {
      const revenueTransactions = allBankData.filter(transaction => transaction.rowType === 1);
      ytdRevenue = revenueTransactions.reduce((sum, t) => sum + (t.deposit || 0), 0);
      const expenseTransactions = allBankData.filter(transaction => transaction.rowType === 1);
      ytdExpense = expenseTransactions.reduce((sum, t) => sum + (t.payment || 0), 0);
    }

    const difference = ytdRevenue - ytdExpense;
    const percentage = ytdExpense > 0 ? ((ytdRevenue - ytdExpense) / ytdExpense) * 100 : 0;
    let trend = 'neutral';
    if (difference > 0) trend = 'up';
    else if (difference < 0) trend = 'down';

    return { trend, difference, percentage };
  }, [allBankData]);

  // Vendor Invoices data - now using real vendor invoice data
  const vendorInvoices = useMemo(() => {
    if (!vendorInvoiceData.length) {
      return {
        due: '$0',
        overdue: '$0'
      };
    }

    // Calculate due invoices (not paid or partially paid)
    const dueInvoices = vendorInvoiceData.filter(invoice => {
      const isPaid = invoice.paidAmount >= invoice.totalAmount;
      const isOverdue = invoice.dueDate && new Date(invoice.dueDate) < new Date();
      return !isPaid && !isOverdue;
    }).reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);

    // Calculate overdue invoices (past due date and not fully paid)
    const overdueInvoices = vendorInvoiceData.filter(invoice => {
      const isPaid = invoice.paidAmount >= invoice.totalAmount;
      const isOverdue = invoice.dueDate && new Date(invoice.dueDate) < new Date();
      return !isPaid && isOverdue;
    }).reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);

    return {
      due: `$${dueInvoices.toLocaleString()}`,
      overdue: `$${overdueInvoices.toLocaleString()}`
    };
  }, [vendorInvoiceData]);

  // Customer Orders data with donut chart - now using real API data
  const customerOrders = useMemo(() => {
    // Use quotation data for open quotations count
    // const openQuotations = quotationData.length;
     const openQuotations = quotationData.filter(order => 
      order.status == null || 
      order.status?.toLowerCase().includes('active')
    ).length;


    // if (!customerOrdersData.length) {
    //   return {
    //     openQuotations,
    //     activeOrders: 0,
    //     shipped: 0
    //   };
    // }

    // Count orders by status
    // const activeOrders = customerOrdersData.filter(order => 
    //   order.status?.toLowerCase().includes('active') || 
    //   order.status?.toLowerCase().includes('pending') ||
    //   order.status?.toLowerCase().includes('processing')
    // ).length;

    // const shipped = customerOrdersData.filter(order => 
    //   order.status?.toLowerCase().includes('shipped') 
    // ).length;
     
    const shipped = customerShippedData.length;
    const activeOrders = dashboardData?.[0]?.readyToShip ?? 0;


    return {
      openQuotations,
      activeOrders,
      shipped
    };
  }, [dashboardData, customerShippedData,quotationData]);

  // Vendor Orders data with donut chart - now using real API data
  const vendorOrders = useMemo(() => {

    // if (!dashboardData.length) {
    //   return {
    //     sent: 0,
    //     pending: 0,
    //     completed: 0
    //   };
    // }

    // Count vendor orders by status
    // const sent = vendorOrdersData.filter(order => 
    //   order.status?.toLowerCase().includes('sent') || 
    //   order.status?.toLowerCase().includes('submitted') ||
    //   order.status?.toLowerCase().includes('approved')
    // ).length;

    // const pending = vendorOrdersData.filter(order => 
    //   order.status?.toLowerCase().includes('pending') || 
    //   order.status?.toLowerCase().includes('waiting') ||
    //   order.status?.toLowerCase().includes('processing')
    // ).length;

    // const completed = vendorOrdersData.filter(order => 
    //   order.status?.toLowerCase().includes('completed') || 
    //   order.status?.toLowerCase().includes('delivered') ||
    //   order.status?.toLowerCase().includes('received')
    // ).length;

    const completed = dashboardData?.[0]?.completedVendorOrderCount ?? 0;
    const all = dashboardData?.[0]?.allVendorOrders ?? 0;
    const sent = dashboardData?.[0]?.vendorRecieving ?? 0;
    const pending = Math.max(0, all - completed - sent); // Ensure pending is never negative
    return {
      sent,
      pending,
      completed
    };
  }, [dashboardData]);

  // Job Stats data - now using real job order data
  const jobStats = useMemo(() => {
    if (!jobOrderData.length) {
      return [
        { label: 'Total Jobs', value: 0, color: '#28a745' },
        { label: 'Running', value: 0, color: '#007bff' },
        { label: 'Due this month', value: 0, color: '#28a745' },
        { label: 'NCR', value: 0, color: '#6c757d' }
      ];
    }

    const totalJobs = jobOrderData.length;
    // const running = jobOrderData.filter(job => 
    //   job.status?.toLowerCase().includes('running') || 
    //   job.status?.toLowerCase().includes('active') ||
    //   job.status?.toLowerCase().includes('in progress')
    // ).length;
    // const ncrFlagged = jobOrderData.filter(job => 
    //   job.ncrFlagged === true || 
    //   job.ncrFlagged === 1 ||
    //   job.status?.toLowerCase().includes('ncr')
    // ).length;
    // const rework = jobOrderData.filter(job => 
    //   job.status?.toLowerCase().includes('rework') ||
    //   job.rework === true ||
    //   job.rework === 1
    // ).length;

    const ncrFlagged =  dashboardJobData?.[0]?.ncr ?? 0;

    
    const running = jobOrderData.filter(job => 
      job.runningSteps > 0
    ).length;

    const rework = jobOrderData.filter(job => 
      job.jobNo?.toLowerCase().includes('rw')
    ).length;

    return [
      { label: 'Total Jobs', value: totalJobs, color: '#28a745' },
      { label: 'Running', value: running, color: '#007bff' },
      { label: 'Due this month', value: ncrFlagged, color: '#28a745' },
      { label: 'NCR', value: rework, color: '#6c757d' }
    ];
  }, [jobOrderData,dashboardJobData]);

  // Tasks data - now using real job order data with step assignments from job order slideout
  // Also includes job-level tasks from JobCreations with Assigned To, Process, and Descriptions
  // Filtered by logged-in user's tenantID
  // If roleId = 1: shows all assigned tasks (still filtered by tenantID)
  // If roleId !== 1: shows only tasks assigned to the logged-in user

  // const tasks = useMemo(() => {
  //   if (!jobOrderData || !jobOrderData.length) {
  //     console.log('No jobOrderData available');
  //     return [];
  //   }

  //   // Get logged-in user's tenantID, roleId, and userId from storage
  //   const storage = JSON.parse(localStorage.getItem("storage") || '{}');
  //   const currentTenantID = storage?.tenantID || 0;
  //   const currentRoleId = storage?.rolId || storage?.roleId || 0;
  //   const currentUserId = storage?.user_UniqueID || storage?.userId || storage?.userID || 0;
  //   const currentUserName = storage?.userName || '';

  //   console.log('Tasks Filter - Current User:', {
  //     tenantID: currentTenantID,
  //     roleId: currentRoleId,
  //     userId: currentUserId,
  //     userName: currentUserName,
  //     locationId: locationId
  //   });
  //   console.log('Total jobOrderData:', jobOrderData.length);
  //   if (jobOrderData.length > 0) {
  //     console.log('Sample job data:', jobOrderData[0]);
  //     console.log('Available process fields in job:', {
  //       tname: jobOrderData[0]?.tname,
  //       templateName: jobOrderData[0]?.templateName,
  //       process: jobOrderData[0]?.process,
  //       jobprocess: jobOrderData[0]?.jobprocess,
  //       processName: jobOrderData[0]?.processName,
  //       processTemplate: jobOrderData[0]?.processTemplate,
  //       allKeys: Object.keys(jobOrderData[0] || {})
  //     });
  //   }

  //   // Filter jobOrderData by tenantID to ensure only tasks from logged-in user's tenant are shown
  //   let filteredJobData = jobOrderData.filter((job: any) => {
  //     const jobTenantID = job.tenantID || job.tenantid || job.tenantId || 0;
      
  //     // Check tenantID match (allow if matches or if currentTenantID is 0 as fallback)
  //     const tenantMatch = jobTenantID === currentTenantID || currentTenantID === 0;
      
  //     return tenantMatch;
  //   });

  //   console.log('After tenantID filter:', filteredJobData.length, 'jobs');
  //   // If roleId = 1: show all assigned tasks (any user, but must have assignment)
  //   // If roleId !== 1: show only tasks assigned to the logged-in user
  //   if (currentRoleId === 1) {
  //     // For roleId = 1, show all jobs that have assignments (any user)
  //     const beforeRoleFilter = filteredJobData.length;
  //     filteredJobData = filteredJobData.filter((job: any) => {
  //       // Check job-level assignment (any user)
  //       const jobAssignedId = job.assignedId || job.jobassignedid || job.assignedUserId || job.jobAssignedId || job.assignedid || job.jobAssignedId || 0;
  //       const jobAssignedTo = job.assignedTo || job.assignedto || job.assignedName || job.jobAssignedTo || '';
  //       const hasJobAssignment = (jobAssignedId > 0) || (jobAssignedTo && jobAssignedTo.trim() !== '' && jobAssignedTo !== 'null' && jobAssignedTo !== 'undefined');
        
  //       // Check step-level assignments (any user)
  //       const steps = job.steps || job.processSteps || job.jobSteps || job.workflowSteps || job.processJobSteps || [];
  //       let hasStepAssignment = false;
        
  //       if (Array.isArray(steps) && steps.length > 0) {
  //         hasStepAssignment = steps.some((step: any) => {
  //           const stepAssignedId = step.assignedId || step.assignedUserId || step.userId || step.workerId || 0;
  //           const stepAssignedTo = step.assignedName || step.processBy || step.assignedTo || step.assignedUserName || 
  //                                 step.assignedUser || step.assignedPerson || step.worker || step.userName || '';
            
  //           return (stepAssignedId > 0) || (stepAssignedTo && stepAssignedTo.trim() !== '' && stepAssignedTo !== 'null' && stepAssignedTo !== 'undefined');
  //         });
  //       }
        
  //       // Include job if it has any assignment (job level or step level)
  //       return hasJobAssignment || hasStepAssignment;
  //     });
  //     console.log('RoleId = 1 - showing all assigned tasks:', filteredJobData.length, 'jobs (removed', beforeRoleFilter - filteredJobData.length, 'unassigned)');
  //   } else if (currentRoleId !== 0) {
  //     // For roleId !== 1 and !== 0, filter by assigned user (only show tasks assigned to the logged-in user)
  //     const beforeRoleFilter = filteredJobData.length;
  //     filteredJobData = filteredJobData.filter((job: any) => {
  //       // Check job-level assignment
  //       const jobAssignedId = job.assignedId || job.jobassignedid || job.assignedUserId || job.jobAssignedId || job.assignedid || job.jobAssignedId || 0;
  //       const jobAssignedTo = job.assignedTo || job.assignedto || job.assignedName || job.jobAssignedTo || '';
        
  //       // Check if job is assigned to current user
  //       const jobAssignedToUser = (jobAssignedId > 0 && jobAssignedId === currentUserId) || 
  //                                 (jobAssignedTo && jobAssignedTo.toLowerCase() === currentUserName.toLowerCase());
        
  //       // Check step-level assignments
  //       const steps = job.steps || job.processSteps || job.jobSteps || job.workflowSteps || job.processJobSteps || [];
  //       let hasStepAssignedToUser = false;
        
  //       if (Array.isArray(steps) && steps.length > 0) {
  //         hasStepAssignedToUser = steps.some((step: any) => {
  //           const stepAssignedId = step.assignedId || step.assignedUserId || step.userId || step.workerId || 0;
  //           const stepAssignedTo = step.assignedName || step.processBy || step.assignedTo || step.assignedUserName || 
  //                                 step.assignedUser || step.assignedPerson || step.worker || step.userName || '';
            
  //           return (stepAssignedId > 0 && stepAssignedId === currentUserId) ||
  //                  (stepAssignedTo && stepAssignedTo.toLowerCase() === currentUserName.toLowerCase());
  //         });
  //       }
        
  //       // Include job if it's assigned to user at job level or has at least one step assigned to user
  //       const include = jobAssignedToUser || hasStepAssignedToUser;
        
  //       if (!include) {
  //         console.log('Job filtered out by role:', {
  //           jobId: job.id,
  //           jobNo: job.jobNo,
  //           jobAssignedId,
  //           currentUserId,
  //           jobAssignedTo,
  //           currentUserName,
  //           hasSteps: steps.length > 0
  //         });
  //       }
        
  //       return include;
  //     });
  //     console.log('After roleId filter:', filteredJobData.length, 'jobs (removed', beforeRoleFilter - filteredJobData.length, ')');
  //   } else {
  //     console.log('RoleId is 0 - showing all assigned tasks');
  //   }

  //   if (!filteredJobData || !filteredJobData.length) {
  //     console.log('No jobs after filtering');
  //     return [];
  //   }

  //   const allTasks: any[] = [];

  //   // Process each job order to extract task assignments from steps and job-level tasks from JobCreations
  //   filteredJobData.forEach((job, jobIndex) => {
  //     // Get job details from API for this job
  //     // Check multiple possible field names for job ID
  //     const jobId = job.id || job.jobId || job.jobid || job.ID || job.JobId || job.JobID || 0;
      
  //     // Prioritize jobNo as main title
  //     const jobNumber = job.jobNo || job.jobCode || job.jobNumber || job.jobId || 
  //                      (job.customerOrderNumber ? `CO#${job.customerOrderNumber}` : null) ||
  //                      (job.orderNumber ? `ORD#${job.orderNumber}` : null) ||
  //                      `JO#${jobId || jobIndex || 'unknown'}`;
  //     const apiJobDetails = jobDetailsMap[jobId] || [];
      
  //     // Debug: Log if job ID is missing
  //     if (!jobId || jobId === 0) {
  //       console.warn('Job missing ID:', {
  //         allKeys: Object.keys(job),
  //         jobNo: job.jobNo,
  //         jobCode: job.jobCode,
  //         jobNumber: job.jobNumber
  //       });
  //     }
      
  //     // For job-level tasks from JobCreations, use the first jobdetail from API if available
  //     // This ensures we get the correct processname and jdescription from the API
  //     let jobLevelProcess = '';
  //     let jobLevelDescription = '';
  //     let jobLevelAssignedTo = '';
  //     let jobLevelAssignedId = 0;
  //     let jobLevelWorkstationId = 0;
      
  //     if (apiJobDetails.length > 0) {
  //       // Use the first jobdetail for job-level task (or aggregate if needed)
  //       const firstJobDetail = apiJobDetails[0];
  //       jobLevelProcess = firstJobDetail.processname || '';
  //       jobLevelDescription = firstJobDetail.jdescription || '';
  //       jobLevelAssignedTo = firstJobDetail.assignedName || '';
  //       jobLevelAssignedId = firstJobDetail.assignedid || 0;
  //       jobLevelWorkstationId = firstJobDetail.workstationid || 0;
        
  //       // Debug logging - removed to reduce console noise
  //       // if (!jobLevelProcess || !jobLevelDescription) {
  //       //   console.log(`Job ${jobId} - Job-level task missing data:`, {
  //       //     processname: firstJobDetail.processname,
  //       //     jdescription: firstJobDetail.jdescription,
  //       //     assignedName: firstJobDetail.assignedName,
  //       //     allKeys: Object.keys(firstJobDetail)
  //       //   });
  //       // }
  //     }
      
  //     // Fallback to job-level fields if API data not available
  //     if (!jobLevelProcess) {
  //       jobLevelProcess = job.tname || job.templateName || 
  //                        (job.jobmaster && job.jobmaster[0]?.tname) ||
  //                        (job.jobmaster && job.jobmaster[0]?.process) ||
  //                        job.process || job.jobprocess || job.processName || 
  //                        job.processTemplate || '';
  //     }
      
  //     if (!jobLevelDescription) {
  //       jobLevelDescription = job.description || job.jobDesc || job.jdescription || job.jobDescription || 
  //                           job.jobDesc || job.desc || '';
  //     }
      
  //     if (!jobLevelAssignedTo) {
  //       jobLevelAssignedTo = job.assignedTo || job.assignedto || job.assignedName || job.jobAssignedTo || 
  //                          job.assignedUserName || job.assignedUser || 
  //                          (job.jobmaster && job.jobmaster[0]?.assignedTo) || '';
  //     }
      
  //     if (!jobLevelAssignedId) {
  //       jobLevelAssignedId = job.assignedId || job.jobassignedid || job.assignedUserId || 
  //                          job.jobAssignedId || job.assignedid || 0;
  //     }
      
  //     if (!jobLevelWorkstationId) {
  //       jobLevelWorkstationId = job.workstationId || 0;
  //     }

  //     // Create job-level task if there's at least an assignedTo OR process from JobCreations
  //     // Make condition less strict - show task if either assignedTo or process exists
  //     const hasAssignedTo = jobLevelAssignedTo && jobLevelAssignedTo.trim() !== '' && 
  //                           jobLevelAssignedTo !== 'null' && jobLevelAssignedTo !== 'undefined';
  //     const hasProcess = jobLevelProcess && jobLevelProcess.trim() !== '' && 
  //                       jobLevelProcess !== 'null' && jobLevelProcess !== 'undefined';
      
  //     // Only create job-level task if we DON'T have step-level tasks from API
  //     // This prevents duplicates - if we have jobdetails from API, we'll create step-level tasks instead
  //     const hasStepLevelTasksFromAPI = apiJobDetails.length > 0;
  //     const hasStepLevelTasksFromJob = (job.steps && job.steps.length > 0) ||
  //                                      (job.processSteps && job.processSteps.length > 0);
  //     const hasStepLevelTasks = hasStepLevelTasksFromAPI || hasStepLevelTasksFromJob;
      
  //     const hasJobLevelAssignment = hasAssignedTo || hasProcess;
      
  //     // Create job-level task ONLY if:
  //     // 1. We have job-level assignment/process AND
  //     // 2. We DON'T have step-level tasks from API (to avoid duplicates)
  //     // 3. This is a fallback when API data is not available
  //     if (hasJobLevelAssignment && !hasStepLevelTasksFromAPI) {
        
  //       // Determine status based on job status
  //       let jobStatus = 'info';
  //       if (job.runningSteps > 0) {
  //         jobStatus = 'warning';
  //       } else if (job.status === 'Not Started' || job.status === 'Pending') {
  //         jobStatus = 'error';
  //       } else if (job.status === 'Completed' || job.manualStatus === 1) {
  //         jobStatus = 'completed';
  //       }

  //       // Generate unique ID - use jobId or fallback to index
  //       const uniqueJobId = jobId || jobIndex || `job-${Date.now()}-${Math.random()}`;
        
  //       // Extract customer name, part number, and part name from job data or API response
  //       const firstJobDetail = apiJobDetails.length > 0 ? apiJobDetails[0] : null;
  //       const customerName = firstJobDetail?.customerName || job.customerName || job.customer || job.buyerName || '';
  //       const partNo = firstJobDetail?.partNo || job.partNo || job.partno || job.partNumber || '';
  //       const partName = firstJobDetail?.partName || job.partName || job.partname || job.partDescription || '';
        
  //       const jobLevelTask = {
  //         id: `job-${uniqueJobId}`,
  //         text: jobLevelProcess && jobLevelDescription ? `${jobLevelProcess} - ${jobLevelDescription}` : 
  //               jobLevelProcess || jobLevelDescription || jobNumber,
  //         assignedTo: jobLevelAssignedTo,
  //         assignedId: jobLevelAssignedId,
  //         workstationId: jobLevelWorkstationId,
  //         workstationName: (jobLevelWorkstationId > 0 && workstationMap) ? (workstationMap[jobLevelWorkstationId] || '') : '',
  //         stepNo: null, // Job-level task, not a step
  //         process: jobLevelProcess,
  //         description: jobLevelDescription,
  //         status: jobStatus,
  //         stepStatus: job.status || 'Pending',
  //         // Job details
  //         jobNo: job.jobNo || job.jobCode || job.jobNumber || `JO#${uniqueJobId}`,
  //         customerOrderNumber: job.customerOrderNumber || job.orderNumber,
  //         customerName: customerName,
  //         partNo: partNo,
  //         partName: partName,
  //         // Date information
  //         startTime: job.startTime || job.startDate,
  //         endTime: job.endTime || job.endDate,
  //         startDate: job.startDate || job.startTime,
  //         endDate: job.endDate || job.endTime,
  //         dueDate: job.dueDate,
  //         // Process details
  //         processDetails: {
  //           stepNo: null,
  //           process: jobLevelProcess,
  //           description: jobLevelDescription,
  //           processBy: jobLevelAssignedTo,
  //           startTime: job.startTime || job.startDate,
  //           endTime: job.endTime || job.endDate,
  //           status: job.status || 'Pending'
  //         },
  //         date: job.startDate ? new Date(job.startDate).toDateString() : 
  //               (job.endDate ? new Date(job.endDate).toDateString() : 
  //               (job.dueDate ? new Date(job.dueDate).toDateString() : new Date().toDateString())),
  //         isJobLevel: true // Flag to indicate this is a job-level task from JobCreations
  //       };

  //       // Filter out tasks that are not assigned (Select, Unassigned, empty, or no assignedId)
  //       const isValidAssignment = jobLevelAssignedTo && 
  //                                 jobLevelAssignedTo.trim() !== '' && 
  //                                 jobLevelAssignedTo.toLowerCase() !== 'select' &&
  //                                 jobLevelAssignedTo.toLowerCase() !== 'unassigned' &&
  //                                 jobLevelAssignedTo !== 'null' &&
  //                                 jobLevelAssignedTo !== 'undefined' &&
  //                                 (jobLevelAssignedId > 0 || jobLevelWorkstationId > 0);
        
  //       // Filter out completed jobs
  //       const isCompleted = job.status === 'Completed' || 
  //                          job.status === 'completed' ||
  //                          job.manualStatus === 1 ||
  //                          (job.completedSteps > 0 && job.runningSteps === 0 && job.totalSteps > 0 && job.completedSteps === job.totalSteps);
        
  //       if (isValidAssignment && !isCompleted) {
  //         allTasks.push(jobLevelTask);
  //       }
  //     }

  //     // Check for steps in various possible properties
  //     // Priority: Use jobdetails from GetJobData API if available, otherwise use steps from jobOrderData
  //     // Only use API jobdetails if we have them (to avoid duplicates with job-level tasks)
  //     const steps = apiJobDetails.length > 0 ? apiJobDetails : 
  //                  (job.steps || job.processSteps || job.jobSteps || job.workflowSteps || job.processJobSteps || 
  //                  job.jobdetails || job.jobDetails || job.jobDetailsData || []);
      
  //     // Create step-level tasks from jobdetails/steps
  //     // Use a Set to track unique task IDs to prevent duplicates
  //     const usedTaskIds = new Set<string>();
      
  //     if (Array.isArray(steps) && steps.length > 0) {
  //       steps.forEach((step: any, stepIndex: number) => {
  //         // Process step assignment data

  //         // Check for ProcessJob assignment fields - comprehensive field detection
  //         const assignedTo = step.assignedName || step.processBy || step.assignedTo || step.assignedUserName || 
  //                           step.assignedUser || step.assignedPerson || step.worker || step.userName;
  //         const assignedId = step.assignedId || step.assignedUserId || step.userId || step.workerId || step.assignedid || 0;
  //         const workstationId = step.workstationId || step.workstation || step.workstationName || step.workstationCode || step.workstationid || 0;
          
  //         // Assignment detection - more flexible
  //         const hasAssignment = (assignedId && assignedId > 0) || 
  //                              (workstationId && workstationId > 0) || 
  //                              (assignedTo && assignedTo.trim() !== '' && assignedTo !== 'null' && assignedTo !== 'undefined');
          
  //         // Include steps with assignments or show all steps
  //         if (hasAssignment || true) {
  //           // Get actual process name from step (like "Material", "Receiving", etc.)
  //           // Priority: processname from GetJobData API, then processName, then process, then name
  //           const stepProcess = step.processname || step.processName || step.process || step.name || '';
  //           // Get description - Priority: jdescription from GetJobData API, then description, then tdescription
  //           const stepDescription = step.jdescription || step.description || step.tdescription || '';
  //           const stepNo = step.stepNo || step.step || step.srno || step.processOrder || stepIndex + 1;
            
  //           // Debug logging for step data - removed to reduce console noise
  //           // if (!stepProcess || !stepDescription) {
  //           //   console.log(`Step ${stepIndex} for job ${job.id} - Missing data:`, {
  //           //     processname: step.processname,
  //           //     processName: step.processName,
  //           //     process: step.process,
  //           //     name: step.name,
  //           //     jdescription: step.jdescription,
  //           //     description: step.description,
  //           //     tdescription: step.tdescription,
  //           //     allKeys: Object.keys(step)
  //           //   });
  //           // }
            
  //           // Only create task if we have a process name (required)
  //           if (!stepProcess || stepProcess.trim() === '') {
  //             console.log(`Skipping step ${stepIndex} for job ${job.id} - no process name`);
  //             return; // Skip steps without process name
  //           }
            
  //           const taskText = stepProcess && stepDescription ? `${stepProcess} - ${stepDescription}` : 
  //                          stepProcess || stepDescription || '';
            
  //           // Determine status based on step status
  //           let status = 'info';
  //           if (step.status === 'In Progress' || step.status === 'Started' || step.status === 'Running') {
  //             status = 'warning';
  //           } else if (step.status === 'Pending' || step.status === 'Waiting' || step.status === 'Unassigned') {
  //             status = 'error';
  //           } else if (step.status === 'Completed' || step.status === 'Finished') {
  //             status = 'completed';
  //           }

  //           // Generate unique ID - use jobId, step id, or fallback to index
  //           const uniqueJobId = jobId || jobIndex || `job-${Date.now()}-${Math.random()}`;
  //           const stepId = step.id || step.jobdetailsid || step.jobdetailsId || stepIndex;
  //           const taskId = `${uniqueJobId}-step-${stepId}`;
            
  //           // Skip if we've already created this task (prevent duplicates)
  //           if (usedTaskIds.has(taskId)) {
  //             console.log(`Skipping duplicate task: ${taskId}`);
  //             return;
  //           }
  //           usedTaskIds.add(taskId);
            
  //           // Get workstation name if available
  //           const workstationName = workstationId > 0 ? 
  //             (step.workstationName || step.workstation || (workstationMap && workstationMap[workstationId] ? workstationMap[workstationId] : '') || '') : '';

  //           const taskData = {
  //             id: taskId,
  //             text: taskText ? `${taskText} (${jobNumber})` : `${jobNumber}`,
  //             assignedTo: assignedTo || 'Unassigned',
  //             assignedId: assignedId || 0,
  //             workstationId: workstationId || 0,
  //             workstationName: workstationName,
  //             stepNo: stepNo,
  //             process: stepProcess,
  //             description: stepDescription,
  //             status: status,
  //             stepStatus: step.status || 'Pending',
  //             // Job details
  //             jobNo: job.jobNo || job.jobCode || job.jobNumber || `JO#${uniqueJobId}`,
  //             customerOrderNumber: job.customerOrderNumber || job.orderNumber,
  //             // Customer and Part details
  //             customerName: step.customerName || job.customerName || job.customer || job.buyerName || '',
  //             partNo: step.partNo || job.partNo || job.partno || job.partNumber || '',
  //             partName: step.partName || job.partName || job.partname || job.partDescription || '',
  //             // Date information
  //             startTime: step.startTime,
  //             endTime: step.endTime,
  //             startDate: step.startDate || step.startTime,
  //             endDate: step.endDate || step.endTime,
  //             dueDate: step.dueDate || job.dueDate,
  //             // Process details
  //             processDetails: {
  //               stepNo: stepNo,
  //               process: stepProcess,
  //               description: stepDescription,
  //               processBy: assignedTo || 'Unassigned',
  //               startTime: step.startTime,
  //               endTime: step.endTime,
  //               status: step.status || 'Pending'
  //             },
  //             date: step.startDate ? new Date(step.startDate).toDateString() :
  //                   (step.endDate ? new Date(step.endDate).toDateString() :
  //                   (step.dueDate ? new Date(step.dueDate).toDateString() : 
  //                   (job.startDate ? new Date(job.startDate).toDateString() :
  //                   (job.endDate ? new Date(job.endDate).toDateString() :
  //                   (job.dueDate ? new Date(job.dueDate).toDateString() : new Date().toDateString()))))),
  //             isJobLevel: false // Flag to indicate this is a step-level task
  //           };

  //           // Filter out tasks that are not assigned (Select, Unassigned, empty, or no assignedId)
  //           const isValidAssignment = assignedTo && 
  //                                     assignedTo.trim() !== '' && 
  //                                     assignedTo.toLowerCase() !== 'select' &&
  //                                     assignedTo.toLowerCase() !== 'unassigned' &&
  //                                     assignedTo !== 'null' &&
  //                                     assignedTo !== 'undefined' &&
  //                                     (assignedId > 0 || workstationId > 0);
            
  //           // Filter out completed steps/jobs
  //           const isCompleted = step.status === 'Completed' || 
  //                              step.status === 'completed' ||
  //                              step.status === 'Finished' ||
  //                              step.status === 'finished' ||
  //                              job.status === 'Completed' ||
  //                              job.status === 'completed' ||
  //                              job.manualStatus === 1;
            
  //           if (isValidAssignment && !isCompleted) {
  //             allTasks.push(taskData);
  //           }
  //         }
  //       });
  //     } else {
  //       // If no steps found AND no API jobdetails, create a basic task from job data if we have job-level info
  //       // This ensures we show something even if steps aren't available
  //       // Only create if we haven't already created a job-level task above
  //       const hasJobLevelData = jobLevelAssignedTo || jobLevelProcess || job.jobNo;
  //       const alreadyCreatedJobLevelTask = hasJobLevelAssignment && !hasStepLevelTasksFromAPI;
        
  //       if (hasJobLevelData && !alreadyCreatedJobLevelTask) {
  //         // Generate unique ID - use jobId or fallback to index
  //         const uniqueJobId = jobId || jobIndex || `job-${Date.now()}-${Math.random()}`;
  //         const basicTaskId = `job-basic-${uniqueJobId}`;
          
  //         // Skip if we've already created this task
  //         if (usedTaskIds.has(basicTaskId)) {
  //           return;
  //         }
  //         usedTaskIds.add(basicTaskId);
          
  //         const basicTask = {
  //           id: basicTaskId,
  //           text: jobLevelProcess && jobLevelDescription ? `${jobLevelProcess} - ${jobLevelDescription}` : 
  //                 jobLevelProcess || jobLevelDescription || jobNumber,
  //           assignedTo: jobLevelAssignedTo || 'Unassigned',
  //           assignedId: jobLevelAssignedId || 0,
  //           workstationId: jobLevelWorkstationId || 0,
  //           workstationName: (jobLevelWorkstationId > 0 && workstationMap) ? (workstationMap[jobLevelWorkstationId] || '') : '',
  //           stepNo: null,
  //           process: jobLevelProcess,
  //           description: jobLevelDescription,
  //           status: job.runningSteps > 0 ? 'warning' : (job.status === 'Completed' ? 'completed' : 'info'),
  //           stepStatus: job.status || 'Pending',
  //           jobNo: job.jobNo || job.jobCode || job.jobNumber || `JO#${uniqueJobId}`,
  //           customerOrderNumber: job.customerOrderNumber || job.orderNumber,
  //           // Customer and Part details
  //           customerName: job.customerName || job.customer || job.buyerName || '',
  //           partNo: job.partNo || job.partno || job.partNumber || '',
  //           partName: job.partName || job.partname || job.partDescription || '',
  //           startTime: job.startTime || job.startDate,
  //           endTime: job.endTime || job.endDate,
  //           startDate: job.startDate || job.startTime,
  //           endDate: job.endDate || job.endTime,
  //           dueDate: job.dueDate,
  //           processDetails: {
  //             stepNo: null,
  //             process: jobLevelProcess,
  //             description: jobLevelDescription,
  //             processBy: jobLevelAssignedTo || 'Unassigned',
  //             startTime: job.startTime || job.startDate,
  //             endTime: job.endTime || job.endDate,
  //             status: job.status || 'Pending'
  //           },
  //           date: job.startDate ? new Date(job.startDate).toDateString() : 
  //               (job.endDate ? new Date(job.endDate).toDateString() : 
  //               (job.dueDate ? new Date(job.dueDate).toDateString() : new Date().toDateString())),
  //           isJobLevel: true
  //         };
          
  //         // Filter out tasks that are not assigned (Select, Unassigned, empty, or no assignedId)
  //         const isValidAssignment = jobLevelAssignedTo && 
  //                                   jobLevelAssignedTo.trim() !== '' && 
  //                                   jobLevelAssignedTo.toLowerCase() !== 'select' &&
  //                                   jobLevelAssignedTo.toLowerCase() !== 'unassigned' &&
  //                                   jobLevelAssignedTo !== 'null' &&
  //                                   jobLevelAssignedTo !== 'undefined' &&
  //                                   (jobLevelAssignedId > 0 || jobLevelWorkstationId > 0);
          
  //         if (isValidAssignment) {
  //           allTasks.push(basicTask);
  //         }
  //       }
  //     }

  //     // Only show real assignments, no fallback fake assignments

  //     // Check for pending approvals
  //     if (job.pendingApproval) {
  //       allTasks.push({
  //         id: `approval-${job.id}`,
  //         text: `Please approve pending items for ${jobNumber}`,
  //         status: 'error',
  //         date: job.dueDate ? new Date(job.dueDate).toDateString() : new Date().toDateString()
  //       });
  //     }
  //   });

  //   // Debug logging - remove in production
  //   console.log('Task Creation Summary:', {
  //     totalJobs: jobOrderData.length,
  //     filteredJobs: filteredJobData.length,
  //     tasksCreated: allTasks.length,
  //     currentRoleId,
  //     currentTenantID,
  //     locationId
  //   });
    
  //   if (allTasks.length === 0 && filteredJobData.length > 0) {
  //     console.log('⚠️ No tasks created from', filteredJobData.length, 'filtered jobs');
  //     console.log('Sample filtered job:', filteredJobData[0]);
  //     console.log('Job has steps?', !!(filteredJobData[0]?.steps || filteredJobData[0]?.processSteps));
  //     console.log('Job assignedTo?', filteredJobData[0]?.assignedTo || filteredJobData[0]?.assignedto);
  //     console.log('Job process?', filteredJobData[0]?.process || filteredJobData[0]?.jobprocess);
  //   } else if (allTasks.length > 0) {
  //     console.log(`✅ Found ${allTasks.length} tasks from ${filteredJobData.length} jobs`);
  //   } else if (filteredJobData.length === 0) {
  //     console.log('⚠️ No jobs passed filtering');
  //   }

  //   // Remove duplicates based on ID before sorting
  //   const uniqueTasks = Array.from(
  //     new Map(allTasks.map(task => [task.id, task])).values()
  //   );
    
  //   console.log(`Task deduplication: ${allTasks.length} tasks before, ${uniqueTasks.length} tasks after`,uniqueTasks);
    
  //   // Sort by priority (error > warning > info > completed) and limit to 10 tasks
  //   return uniqueTasks
  //     .sort((a, b) => {
  //       const statusOrder = { 'error': 0, 'warning': 1, 'info': 2, 'completed': 3 };
  //       return statusOrder[a.status] - statusOrder[b.status];
  //     })
  //     .slice(0, 10);
  //   }, [jobOrderData, jobDetailsMap]);



  // Filter tasks based on search term - tasks
  const filteredTasks = useMemo(() => {
    if (!taskSearchTerm || taskSearchTerm.trim() === '') {
      return tasks;
    }
    
    const searchLower = taskSearchTerm.toLowerCase().trim();
    return tasks.filter(task => {
      // Search in job number
      const jobNoMatch = task.jobNo?.toLowerCase().includes(searchLower);
      // Search in process
      const processMatch = task.processname?.toLowerCase().includes(searchLower);
      // Search in description
      const descriptionMatch = task.description?.toLowerCase().includes(searchLower);
      // Search in assigned to
      const assignedMatch = task.assignedBy?.toLowerCase().includes(searchLower);
      // Search in customer order number
      const customerOrderMatch = task.customerOrderNumber?.toLowerCase().includes(searchLower);
      // Search in workstation name
      const workstationMatch = task.workStationName?.toLowerCase().includes(searchLower);
      // Search in customer name
      const customerNameMatch = task.customerName?.toLowerCase().includes(searchLower);
      // Search in part number
      const partNoMatch = task.partNo?.toLowerCase().includes(searchLower);
      // Search in part name
      const partNameMatch = task.partName?.toLowerCase().includes(searchLower);
      
      return jobNoMatch || processMatch || descriptionMatch || assignedMatch || customerOrderMatch || workstationMatch || customerNameMatch || partNoMatch || partNameMatch;
    });
  }, [tasks, taskSearchTerm]);




  // Timeline data - now using real comments data
  const timeline = useMemo(() => {
    if (!commentsData || commentsData.length === 0) {
      return [];
    }


    const timelineItems = commentsData.slice(0, 5).map((comment: any, index: number) => {
      // Determine comment type based on content or entryType
      let type = 'info';
      const entryTypeLower = comment.entryType?.toLowerCase() || '';
      if (entryTypeLower === 'customerorder' || entryTypeLower === 'customer order') {
        type = 'message';
      } else if (comment.comments?.toLowerCase().includes('@')) {
        type = 'alert'; // Mentions are alerts
      } else if (comment.comments?.toLowerCase().includes('urgent') || comment.comments?.toLowerCase().includes('important')) {
        type = 'warning';
      }

      // Format date using the API's createdOn field
      const commentDate = comment.createdOn;
      const date = commentDate ? new Date(commentDate).toDateString() : new Date().toDateString();
      
      // Calculate time ago
      const timeAgo = commentDate ? 
        (() => {
          const now = new Date();
          const commentTime = new Date(commentDate);
          const diffInHours = Math.floor((now.getTime() - commentTime.getTime()) / (1000 * 60 * 60));
          
          if (diffInHours < 1) return 'Just now';
          if (diffInHours < 24) return `${diffInHours} hours ago`;
          const diffInDays = Math.floor(diffInHours / 24);
          if (diffInDays < 7) return `${diffInDays} days ago`;
          return date;
        })() : 'Unknown';

      // Extract author from createdBy - use userMap to get actual user name
      const userId = comment.createdBy || 0;
      const author = userMap[userId] || `User ${userId}`;
      
      // Determine if message is sent or received - ensure type consistency
      const commentUserId = typeof userId === 'string' ? parseInt(userId, 10) : (userId || 0);
      const loggedInUserId = typeof currentUserId === 'string' ? parseInt(currentUserId, 10) : (currentUserId || 0);
      const isSent = commentUserId === loggedInUserId && loggedInUserId > 0;
      const messageType = isSent ? 'sent' : 'received';
      return {
        // type: type,
        type:'message',
        content: comment.comments || 'No content',
        date: date,
        timeAgo: timeAgo,
        author: author,
        messageType: messageType, // 'sent' or 'received'
        id: comment.id,
        entryType: comment.entryType,
        uniqueNo: comment.uniqueNo,
        readed: comment.readed,
        mailSent: comment.mailSent,
        msgnumber:comment.number,
        partid:comment.partid,
        orderid:comment.orderid
      };
    });
    
    return timelineItems;
  }, [commentsData, userMap, currentUserId]);

  // Jobs data - now using real job order data
  const jobs = useMemo(() => {
    if (!jobOrderData.length) {
      return [];
    }

    return jobOrderData.map(job => {
      const progress = job.progressPercentage || 0;
      const complete = job.completedSteps || 0;
      const total = job.totalSteps || 0;
      const dueDate = job.dueDate ? new Date(job.dueDate) : null;
      const daysDue = dueDate ? Math.ceil((dueDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;
      const status = job.manualTracking ? job.manualStatus === 0 ? 'Ready to Ship' : 'Completed'  : job.runningSteps > 0 ? 'Running' : job.runningSteps === 0 && job.completedSteps > 0 ? 'Completed' : 'Not Started';

      // Get job ID for this job
      const jobIdForStats = job.id || job.jobId || job.jobid || job.ID || job.JobId || job.JobID || 0;

      // Prioritize jobNo as main title
      const jobNumber = job.jobNo || job.jobCode || job.jobNumber || job.jobId || 
                       (job.customerOrderNumber ? `CO#${job.customerOrderNumber}` : null) ||
                       (job.orderNumber ? `ORD#${job.orderNumber}` : null) ||
                       `JO#${jobIdForStats || 'unknown'}`;

      return {
        id: jobNumber,
        status: status || 'Unknown',
        progress: Math.max(0, Math.min(100, progress)),
        complete,
        total,
        daysDue: Math.max(0, daysDue)
      };
    });
  }, [jobOrderData]);

  
  // Filter tasks based on search term - jobs
 const filteredJobs = useMemo(() => {
  let filtered = jobs;
  if (jobSearchTerm && jobSearchTerm.trim() !== '') {
    const searchLower = jobSearchTerm.toLowerCase().trim();
    filtered = jobs.filter(task => {
      const jobNoMatch = task.id?.toLowerCase().includes(searchLower);
      const statusMatch = task.status?.toLowerCase().includes(searchLower);
      return jobNoMatch || statusMatch;
    });
  }

  if (!showAllJobs) {
    filtered = filtered.slice(0, 10);
  }

  return filtered;
}, [jobs, jobSearchTerm, showAllJobs]);



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return '#28a745';
      case 'Not Started': return '#dc3545';
      case 'Ready to Ship': return '#ffc107';
      case 'Running': return '#28a745';
      case 'Suspended': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'alert': return <MdError style={{ color: '#dc3545' }} />;
      case 'message': return <MdInfo style={{ color: '#007bff' }} />;
      case 'warning': return <MdWarning style={{ color: '#ffc107' }} />;
      default: return <MdInfo />;
    }
  };

  const handleReferenceClick = (item) => {
  setSlideout({
    open: true,
    type: item.entryType,
    data: item,
  });
 };

   const handleJobClick = (job) => {
   setSlideout({
    open: true,
    type: "JobOrderTask",
    data: job,
  });
  };

  const closeSlideout = () => {
  setSlideout({ open: false, type: "", data: null });
};

  // Professional and mobile responsive styles — memoized to prevent object recreation on every render
  const styles = useMemo(() => ({
    dashboard: {
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      padding: '0',
      lineHeight: 1.5
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      width: '100%',
      padding: 'clamp(1rem, 2vw, 2rem)'
    },
    headerSection: {
      background: 'white',
      borderRadius: '8px',
      padding: 'clamp(1.5rem, 3vw, 2rem)',
      marginBottom: 'clamp(1.5rem, 3vw, 2rem)',
      color: '#1f2937',
      position: 'relative' as const,
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '2rem',
      position: 'relative' as const,
      zIndex: 2
    },
    header: {
      fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
      fontWeight: 800,
      color: '#1f2937',
      marginBottom: '0.5rem',
      letterSpacing: '-0.025em'
    },
    headerSubtitle: {
      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
      color: '#6b7280',
      margin: 0,
      fontWeight: 400,
      lineHeight: 1.4
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    lastUpdated: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'flex-end',
      gap: '0.25rem'
    },
    lastUpdatedLabel: {
      fontSize: 'clamp(0.7rem, 1.2vw, 0.75rem)',
      color: '#6b7280',
      fontWeight: 500
    },
    lastUpdatedTime: {
      fontSize: 'clamp(0.8rem, 1.5vw, 0.875rem)',
      color: '#1f2937',
      fontWeight: 600
    },
    metricsSummary: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 'clamp(1rem, 2vw, 1.5rem)',
      marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
    },
    metricCard: {
      background: 'white',
      borderRadius: '12px',
      padding: 'clamp(1rem, 2vw, 1.25rem)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(0.75rem, 1.5vw, 1rem)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer'
    },
    metricIcon: {
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      width: 'clamp(3rem, 6vw, 4rem)',
      height: 'clamp(3rem, 6vw, 4rem)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f3f4f6',
      borderRadius: '12px',
      color: '#6b7280'
    },
    metricContent: {
      flex: 1,
      minWidth: 0
    },
    metricValue: {
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: 800,
      color: '#1f2937',
      lineHeight: 1,
      marginBottom: '0.25rem'
    },
    metricLabel: {
      fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
      color: '#6b7280',
      fontWeight: 500,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em'
    },
    sectionTitle: {
      background: 'white',
      borderRadius: '12px',
      padding: 'clamp(0.5rem, 1.5vw, 1rem)',
      marginBottom: 'clamp(0.75rem, 1.5vw, 1rem)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    },
    sectionTitleText: {
      fontSize: 'clamp(1rem, 2vw, 1.125rem)',
      fontWeight: 700,
      color: '#1f2937',
      marginBottom: '0.25rem',
      letterSpacing: '-0.025em'
    },
    sectionSubtitle: {
      fontSize: 'clamp(0.75rem, 1.2vw, 0.875rem)',
      color: '#6b7280',
      margin: 0,
      fontWeight: 400,
      lineHeight: 1.4
    },
    topRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: 'clamp(0.75rem, 1.5vw, 1rem)',
      marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
    },
    bottomRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: 'clamp(1rem, 2vw, 1.5rem)'
    },
    card: {
      background: '#fff',
      borderRadius: '8px',
      padding: 'clamp(0.75rem, 1.5vw, 1rem)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative' as const,
      overflow: 'hidden',
      cursor: 'pointer'
    },
    cardTitle: {
      fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
      fontWeight: 600,
      color: '#1f2937',
      marginBottom: 'clamp(0.5rem, 1vw, 0.75rem)',
      letterSpacing: '-0.025em',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    paymentCard: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 'clamp(0.25rem, 0.5vw, 0.5rem)'
    },
    paymentItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.25rem 0',
      borderBottom: '1px solid #f3f4f6'
    },
    paymentLabel: {
      fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)',
      color: '#6b7280',
      fontWeight: 500
    },
    paymentValue: {
      fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
      fontWeight: 700,
      color: '#111827',
      letterSpacing: '-0.025em'
    },
    paymentIndicator: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      marginLeft: '0.5rem'
    },
    donutChart: {
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(0.5rem, 1vw, 0.75rem)'
    },
    donutContainer: {
      width: '60px',
      height: '60px',
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    donutLegend: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 'clamp(0.25rem, 0.5vw, 0.375rem)'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(0.25rem, 0.5vw, 0.375rem)',
      fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)'
    },
    legendDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%'
    },
    jobStatsList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 'clamp(0.375rem, 0.75vw, 0.5rem)'
    },
    jobStatItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.125rem 0'
    },
    jobStatBadge: {
      padding: '0.25rem 0.5rem',
      borderRadius: '12px',
      fontSize: 'clamp(0.7rem, 1.2vw, 0.75rem)',
      fontWeight: 500,
      color: 'white'
    },
    searchInput: {
      width: '100%',
      padding: 'clamp(0.5rem, 1vw, 0.75rem)',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
      marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
      transition: 'border-color 0.2s ease-in-out',
      outline: 'none',
      backgroundColor: '#fafafa'
    },
    taskItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'clamp(0.5rem, 1vw, 0.75rem)',
      padding: 'clamp(0.5rem, 1vw, 0.75rem) 0',
      borderBottom: '1px solid #f3f4f6',
      transition: 'background-color 0.2s ease-in-out'
    },
    taskContent: {
      flex: 1
    },
    taskText: {
      fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
      color: '#374151',
      marginBottom: '0.25rem',
      lineHeight: 1.4
    },
    taskDate: {
      fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)',
      color: '#6b7280',
      fontWeight: 500
    },
    taskStatus: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      marginTop: '0.25rem'
    },
    timelineItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'clamp(0.5rem, 1vw, 0.75rem)',
      padding: 'clamp(0.5rem, 1vw, 0.75rem) 0',
      borderBottom: '1px solid #f3f4f6',
      transition: 'background-color 0.2s ease-in-out'
    },
    timelineContent: {
      flex: 1,
      minWidth: 0
    },
    timelineHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(0.25rem, 0.5vw, 0.5rem)',
      marginBottom: '0.25rem',
      flexWrap: 'wrap' as const
    },
    timelineBadge: {
      padding: 'clamp(0.125rem, 0.3vw, 0.25rem) clamp(0.375rem, 1vw, 0.5rem)',
      borderRadius: '12px',
      fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)',
      fontWeight: 600,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      whiteSpace: 'nowrap' as const
    },
    timelineText: {
      fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
      color: '#374151',
      marginBottom: '0.25rem',
      lineHeight: 1.4,
      wordBreak: 'break-word' as const
    },
    timelineDate: {
      fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)',
      color: '#6b7280',
      fontWeight: 500
    },
    jobItem: {
      padding: 'clamp(0.75rem, 1.5vw, 1rem) 0',
      borderBottom: '1px solid #f3f4f6',
      transition: 'background-color 0.2s ease-in-out'
    },
    jobHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem'
    },
    jobId: {
      fontSize: 'clamp(0.8rem, 1.5vw, 0.875rem)',
      fontWeight: 600,
      color: '#212529'
    },
    jobStatus: {
      padding: '0.25rem 0.5rem',
      borderRadius: '12px',
      fontSize: 'clamp(0.7rem, 1.2vw, 0.75rem)',
      fontWeight: 500,
      color: 'white'
    },
    progressBar: {
      width: '100%',
      height: '6px',
      backgroundColor: '#e9ecef',
      borderRadius: '3px',
      marginBottom: '0.5rem',
      overflow: 'hidden'
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#007bff',
      borderRadius: '3px',
      transition: 'width 0.3s ease'
    },
    jobMetrics: {
      display: 'flex',
      gap: '1rem',
      fontSize: 'clamp(0.7rem, 1.2vw, 0.75rem)',
      color: '#6c757d'
    },
    viewAllButton: {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '20px',
    padding: '0.3rem 0.7rem',
    cursor: 'pointer',
    fontSize: '0.7rem',
    display: 'inline-flex',
    gap: '4px'  ,
    alignItems: 'center'
  }
  }), []);

  const KpiMetricsSkeleton = (
    <>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} style={styles.metricCard}>
          <SkeletonBox width="36px" height="36px" borderRadius="8px" />
          <div style={{ ...styles.metricContent, flex: 1 }}>
            <SkeletonBox width="60%" height="22px" style={{ marginBottom: '6px' }} />
            <SkeletonBox width="40%" height="12px" />
          </div>
        </div>
      ))}
    </>
  );

  const PaymentCardSkeleton = (
    <div style={styles.paymentCard}>
      <div style={styles.paymentItem}>
        <SkeletonBox width="50%" height="14px" />
        <SkeletonBox width="30%" height="18px" />
      </div>
      <div style={styles.paymentItem}>
        <SkeletonBox width="40%" height="14px" />
        <SkeletonBox width="35%" height="18px" />
      </div>
    </div>
  );

  const DonutChartSkeleton = (
    <div style={styles.donutChart}>
      <div style={styles.donutContainer}>
        <div className="skeleton-box" style={{ width: 60, height: 60, borderRadius: '50%' }} />
      </div>
      <div style={styles.donutLegend}>
        <div style={styles.legendItem}><SkeletonBox width="80px" height="14px" /></div>
        <div style={styles.legendItem}><SkeletonBox width="100px" height="14px" /></div>
        <div style={styles.legendItem}><SkeletonBox width="70px" height="14px" /></div>
      </div>
    </div>
  );

  const JobStatsSkeleton = (
    <div style={styles.jobStatsList}>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} style={styles.jobStatItem}>
          <SkeletonBox width="45%" height="14px" />
          <SkeletonBox width="28px" height="20px" borderRadius="10px" />
        </div>
      ))}
    </div>
  );

  const TasksListSkeleton = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ padding: '12px', borderRadius: '8px', border: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SkeletonBox width="70%" height="16px" />
          <SkeletonBox width="45%" height="14px" />
          <SkeletonBox width="30%" height="12px" />
        </div>
      ))}
    </div>
  );

  const JobsListSkeleton = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
      {[1, 2, 3].map((i) => (
        <div key={i} style={styles.jobItem}>
          <div style={styles.jobHeader}>
            <SkeletonBox width="35%" height="16px" />
            <SkeletonBox width="60px" height="20px" borderRadius="10px" />
          </div>
          <SkeletonBox width="100%" height="6px" borderRadius="3px" style={{ marginBottom: '8px' }} />
          <div style={{ display: 'flex', gap: '12px' }}>
            <SkeletonBox width="25%" height="12px" />
            <SkeletonBox width="25%" height="12px" />
            <SkeletonBox width="25%" height="12px" />
          </div>
        </div>
      ))}
    </div>
  );

  const MessagesListSkeleton = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <SkeletonBox width="32px" height="32px" borderRadius="50%" />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <SkeletonBox width="40%" height="14px" />
            <SkeletonBox width="90%" height="12px" />
            <SkeletonBox width="25%" height="10px" />
          </div>
        </div>
      ))}
    </div>
  );

  // Render dashboard immediately; section data fills in as each request completes.
  // Failures are logged only — do not blank the whole page (isolates impact to this screen).
  const metricsLoading =
    sectionLoading.dashboard ||
    sectionLoading.bank ||
    sectionLoading.jobs ||
    sectionLoading.dashboardJobs;
  const paymentsLoading = sectionLoading.dashboard;
  const customerOrdersLoading =
    sectionLoading.dashboard || sectionLoading.shipped || sectionLoading.quotations;
  const jobStatsLoading = sectionLoading.jobs || sectionLoading.dashboardJobs;
  const vendorOrdersLoading = sectionLoading.dashboard;
  const vendorInvoicesLoading = sectionLoading.vendorInvoices;

  return (
    <>
      <style>
        {`
          @keyframes dashboard-shimmer {
            0% { background-position: -200px 0; }
            100% { background-position: 200px 0; }
          }
          .skeleton-box {
            background: #e9ecef;
            background-image: linear-gradient(90deg, #e9ecef 0px, #f8f9fa 40px, #e9ecef 80px);
            background-size: 400px 100%;
            animation: dashboard-shimmer 1.5s infinite ease-in-out;
            display: inline-block;
          }
          @keyframes dashboard-section-spin {
            to { transform: rotate(360deg); }
          }
          .dashboard-section-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid #e9ecef;
            border-top-color: #007bff;
            border-radius: 50%;
            animation: dashboard-section-spin 0.7s linear infinite;
            display: inline-block;
            flex-shrink: 0;
          }
          @media (max-width: 1200px) {
            .dashboard-grid {
              grid-template-columns: repeat(3, 1fr) !important;
            }
          }
          @media (max-width: 768px) {
            .dashboard-card:hover {
              transform: none !important;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
            }
            .dashboard-card {
              margin-bottom: 1rem;
            }
            .dashboard-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 0.75rem !important;
            }
            .dashboard-header {
              font-size: 1.5rem !important;
              margin-bottom: 1.5rem !important;
            }
          }
          @media (max-width: 480px) {
            .dashboard-grid {
              grid-template-columns: 1fr !important;
            }
          }
          @media (max-width: 480px) {
            .dashboard-container {
              padding: 1rem !important;
            }
            .dashboard-card {
              padding: 1rem !important;
              border-radius: 8px !important;
            }
          }
          .dashboard-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
        `}
      </style>
      <div style={styles.dashboard}>
        <div style={styles.container} className="dashboard-container">
          {/* Header Section aligned with other pages */}
          <div className="page-heading d-flex">
            Dashboard
            <div className="action-group d-flex flex-row ml-auto">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span style={styles.lastUpdatedLabel}>Last Updated:</span>
                <span style={styles.lastUpdatedTime}>{currentTimeTz}</span>
                {isRefreshing && (
                  <span style={{ fontSize: '0.7rem', color: '#007bff', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                    <span className="dashboard-section-spinner" style={{ width: 10, height: 10 }} /> Refreshing…
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Key Metrics Summary */}
          {hasRevenuePermission && 
          <div style={styles.metricsSummary}>
            {metricsLoading ? KpiMetricsSkeleton : (
              <>
            {/* YTD Revenue */}
            <div style={styles.metricCard}>
              <div style={styles.metricIcon}>💰</div>
              <div style={styles.metricContent}>
                <div style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)', fontWeight: 700, color: '#1f2937', lineHeight: 1, marginBottom: '0.25rem' }}>
                  {customerPayments.thisMonth}
                </div>
                <div style={styles.metricLabel}>YTD Revenue</div>
                <div style={{ display: 'none' }}>
                  {ytdComparison.trend === 'up' && `+${ytdComparison.percentage.toFixed(1)}% Profit`}
                  {ytdComparison.trend === 'down' && `${ytdComparison.percentage.toFixed(1)}% Loss`}
                  {ytdComparison.trend === 'neutral' && (ytdComparison.difference === 0 && ytdComparison.percentage === 0 ? 'No Data' : 'Break-even')}
                </div>
              </div>
            </div>

            {/* Active Orders */}
            <div style={styles.metricCard}>
              <div style={styles.metricIcon}>📊</div>
              <div style={styles.metricContent}>
                <div style={styles.metricValue}>{customerOrders.activeOrders}</div>
                <div style={styles.metricLabel}>Active Orders</div>
              </div>
            </div>

            {/* Active Jobs */}
            <div style={styles.metricCard}>
              <div style={styles.metricIcon}>⚡</div>
              <div style={styles.metricContent}>
                <div style={styles.metricValue}>{jobStats.find(s => s.label === 'Running')?.value || 0}</div>
                <div style={styles.metricLabel}>Active Jobs</div>
              </div>
            </div>

            {/* YTD Expense */}
            <div style={styles.metricCard}>
              <div style={styles.metricIcon}>💸</div>
              <div style={styles.metricContent}>
                <div style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)', fontWeight: 700, color: '#1f2937', lineHeight: 1, marginBottom: '0.25rem' }}>
                  {ytdExpenses}
                </div>
                <div style={styles.metricLabel}>YTD Expense</div>
                <div style={{ display: 'none' }}>
                  {ytdComparison.trend === 'up' && `+$${Math.abs(ytdComparison.difference).toLocaleString()}`}
                  {ytdComparison.trend === 'down' && `-$${Math.abs(ytdComparison.difference).toLocaleString()}`}
                  {ytdComparison.trend === 'neutral' && (ytdComparison.difference === 0 && ytdComparison.percentage === 0 ? '$0 (No Data)' : '$0')}
                </div>
              </div>
            </div>
              </>
            )}
          </div>
          }
          {hasOrderPermission && <>
          {/* Section Title */}
          <div style={styles.sectionTitle}>
            <h2 style={styles.sectionTitleText}>Detailed Analytics</h2>
            <p style={styles.sectionSubtitle}>Comprehensive breakdown of business metrics and performance indicators</p>
          </div>
        
        {/* Top Row */}
        
        <div style={styles.topRow} className="dashboard-grid">
          {/* Customer Payments */}
          <div style={styles.card} className="dashboard-card">
            <h3 style={styles.cardTitle}>Customer Payments</h3>
            {paymentsLoading ? PaymentCardSkeleton : (
            <div style={styles.paymentCard}>
              <div style={styles.paymentItem}>
                <span style={styles.paymentLabel}>Received (MTD)</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={styles.paymentValue}>{customerPayments.today}</span>
                  <div style={{ ...styles.paymentIndicator, backgroundColor: '#007bff' }}></div>
                </div>
              </div>
              <div style={styles.paymentItem}>
                <span style={styles.paymentLabel}>Due</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={styles.paymentValue}>{customerPayments.due}</span>
                  <div style={{ ...styles.paymentIndicator, backgroundColor: '#20c997' }}></div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Customer Orders */}
          <div style={styles.card} className="dashboard-card">
            <h3 style={styles.cardTitle}>Customer Orders</h3>
            {customerOrdersLoading ? DonutChartSkeleton : (
            <div style={styles.donutChart}>
              <div style={styles.donutContainer}>
                <svg width="60" height="60" viewBox="0 0 40 40">
                  <circle cx="20" cy="20" r="15" fill="none" stroke="#e9ecef" strokeWidth="8"/>
                  {(() => {
                    const circumference = 94.2;
                    const oq = customerOrders.openQuotations || 0;
                    const ao = customerOrders.activeOrders || 0;
                    const sh = customerOrders.shipped || 0;
                    const total = oq + ao + sh;
                    const safeTotal = total > 0 ? total : 1;
                    const oqLen = (oq / safeTotal) * circumference;
                    const aoLen = (ao / safeTotal) * circumference;
                    const shLen = (sh / safeTotal) * circumference;
                    const aoOffset = -oqLen;
                    const shOffset = -(oqLen + aoLen);
                    return (
                      <>
                        <circle cx="20" cy="20" r="15" fill="none" stroke="#dc3545" strokeWidth="8" 
                          strokeDasharray={`${oqLen} ${circumference}`} 
                          strokeDashoffset="0" transform="rotate(-90 20 20)"/>
                        <circle cx="20" cy="20" r="15" fill="none" stroke="#fd7e14" strokeWidth="8" 
                          strokeDasharray={`${aoLen} ${circumference}`} 
                          strokeDashoffset={`${aoOffset}`} transform="rotate(-90 20 20)"/>
                        <circle cx="20" cy="20" r="15" fill="none" stroke="#28a745" strokeWidth="8" 
                          strokeDasharray={`${shLen} ${circumference}`} 
                          strokeDashoffset={`${shOffset}`} transform="rotate(-90 20 20)"/>
                      </>
                    );
                  })()}
                </svg>
              </div>
              <div style={styles.donutLegend}>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendDot, backgroundColor: '#dc3545' }}></div>
                  <span>{customerOrders.openQuotations} Quotation</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendDot, backgroundColor: '#fd7e14' }}></div>
                  <span>{customerOrders.activeOrders} Active Orders</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendDot, backgroundColor: '#28a745' }}></div>
                  <span>{customerOrders.shipped} Shipped</span>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Job Stats */}
          <div style={styles.card} className="dashboard-card">
            <h3 style={styles.cardTitle}>Job Stats</h3>
            {jobStatsLoading ? JobStatsSkeleton : (
            <div style={styles.jobStatsList}>
              {jobStats.map((stat, index) => (
                <div key={index} style={styles.jobStatItem}>
                  <span style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.875rem)', color: '#6c757d' }}>{stat.label}</span>
                  <span style={{ ...styles.jobStatBadge, backgroundColor: stat.color }}>
                    {stat.value}
                  </span>
            </div>
              ))}
            </div>
            )}
          </div>

          {/* Vendor Orders */}
          <div style={styles.card} className="dashboard-card">
            <h3 style={styles.cardTitle}>Vendor Orders</h3>
            {vendorOrdersLoading ? DonutChartSkeleton : (
            <div style={styles.donutChart}>
              <div style={styles.donutContainer}>
                <svg width="60" height="60" viewBox="0 0 40 40">
                  <circle cx="20" cy="20" r="15" fill="none" stroke="#e9ecef" strokeWidth="8"/>
                  {(() => {
                    const circumference = 94.2;
                    const sent = vendorOrders.sent || 0;
                    const pending = vendorOrders.pending || 0;
                    const completed = vendorOrders.completed || 0;
                    const total = sent + pending + completed;
                    const safeTotal = total > 0 ? total : 1;
                    const sentLen = (sent / safeTotal) * circumference;
                    const pendLen = (pending / safeTotal) * circumference;
                    const compLen = (completed / safeTotal) * circumference;
                    const pendOffset = -sentLen;
                    const compOffset = -(sentLen + pendLen);
                    return (
                      <>
                        <circle cx="20" cy="20" r="15" fill="none" stroke="#20c997" strokeWidth="8" 
                          strokeDasharray={`${sentLen} ${circumference}`} 
                          strokeDashoffset="0" transform="rotate(-90 20 20)"/>
                        <circle cx="20" cy="20" r="15" fill="none" stroke="#6f42c1" strokeWidth="8" 
                          strokeDasharray={`${pendLen} ${circumference}`} 
                          strokeDashoffset={`${pendOffset}`} transform="rotate(-90 20 20)"/>
                        <circle cx="20" cy="20" r="15" fill="none" stroke="#17a2b8" strokeWidth="8" 
                          strokeDasharray={`${compLen} ${circumference}`} 
                          strokeDashoffset={`${compOffset}`} transform="rotate(-90 20 20)"/>
                      </>
                    );
                  })()}
                </svg>
              </div>
              <div style={styles.donutLegend}>
                   <div style={styles.legendItem}>
                  <div style={{ ...styles.legendDot, backgroundColor: '#6f42c1' }}></div>
                  <span>{vendorOrders.pending} Created</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendDot, backgroundColor: '#20c997' }}></div>
                  <span>{vendorOrders.sent} Sent</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendDot, backgroundColor: '#17a2b8' }}></div>
                  <span>{vendorOrders.completed} Completed</span>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Vendor Invoices */}
          <div style={styles.card} className="dashboard-card">
            <h3 style={styles.cardTitle}>Vendor Invoices</h3>
            {vendorInvoicesLoading ? PaymentCardSkeleton : (
            <div style={styles.paymentCard}>
              <div style={styles.paymentItem}>
                <span style={styles.paymentLabel}>Due</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={styles.paymentValue}>{vendorInvoices.due}</span>
                  <div style={{ ...styles.paymentIndicator, backgroundColor: '#fd7e14' }}></div>
                </div>
              </div>
              <div style={styles.paymentItem}>
                <span style={styles.paymentLabel}>Overdue</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={styles.paymentValue}>{vendorInvoices.overdue}</span>
                  <div style={{ ...styles.paymentIndicator, backgroundColor: '#dc3545' }}></div>
                </div>
              </div>
            </div>
            )}
        </div>
        </div>
        </>
        } 

        {/* Bottom Row */}
        <div style={styles.bottomRow}>
          {/* Tasks */}
          <div style={styles.card} className="dashboard-card">
            <h3 style={styles.cardTitle}>Tasks</h3>
            <input 
              type="text" 
              placeholder="Search Tasks..." 
              style={styles.searchInput}
              value={taskSearchTerm}
              onChange={(e) => setTaskSearchTerm(e.target.value)}
            />
            <div>
              {sectionLoading.tasks ? TasksListSkeleton : filteredTasks.length > 0 ? (
                filteredTasks.map((task) => {
                  // const processValue = typeof task.process === 'string' ? task.process.trim() : '';
                  // const descriptionValue = typeof task.description === 'string' ? task.description.trim() : '';
                  // const taskTextValue = typeof task.text === 'string' ? task.text.trim() : '';
                  
                  // const genericProcessValues = ['task', 'job process', 'process', 'job'];
                  // const shouldShowProcess = !!processValue && !genericProcessValues.includes(processValue.toLowerCase());
                  
                  // const genericDescriptionValues = ['no description', 'description', 'task', 'job process', 'process'];
                  // const isDescriptionSameAsProcess = descriptionValue && processValue && descriptionValue.toLowerCase() === processValue.toLowerCase();
                  // const isDescriptionSameAsText = descriptionValue && taskTextValue && descriptionValue.toLowerCase() === taskTextValue.toLowerCase();
                  // const shouldShowDescription = !!descriptionValue && 
                  //   !genericDescriptionValues.includes(descriptionValue.toLowerCase()) &&
                  //   !isDescriptionSameAsProcess &&
                  //   !isDescriptionSameAsText;
                  
                //   return (
                  // <div key={task.id} style={styles.taskItem}>
                  //   <div style={styles.taskContent}>
                  //     <div style={styles.taskText}>
                  //       {/* Job Number and Customer Order */}
                  //       <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: '#007bff' }}>
                  //         {task.jobNo || 'Job Number'}
                  //         {task.customerOrderNumber && (
                  //           <span style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)', color: '#6c757d', marginLeft: '0.5rem' }}>
                  //             ({task.customerOrderNumber})
                  //           </span>
                  //         )}
                  //       </div>
                        
                  //       {/* Customer Name */}
                  //       {task.customerName && (
                  //         <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)', color: '#495057', marginBottom: '0.2rem' }}>
                  //           <strong>Customer:</strong> {task.customerName}
                  //         </div>
                  //       )}
                        
                  //       {/* Part Number and Part Name */}
                  //       {(task.partNo || task.partName) && (
                  //         <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)', color: '#495057', marginBottom: '0.2rem' }}>
                  //           {task.partNo && <span><strong>Part No:</strong> {task.partNo}</span>}
                  //           {task.partNo && task.partName && <span style={{ marginLeft: '0.5rem' }}>|</span>}
                  //           {task.partName && <span style={{ marginLeft: task.partNo ? '0.5rem' : '0' }}><strong>Part Name:</strong> {task.partName}</span>}
                  //         </div>
                  //       )}
                        
                  //       {/* Job-Level Task Indicator from JobCreations */}
                  //       {task.isJobLevel && (
                  //         <div style={{ fontSize: 'clamp(0.65rem, 1vw, 0.75rem)', marginBottom: '0.25rem' }}>
                  //           <span style={{ 
                  //             padding: '0.2em 0.5em', 
                  //             borderRadius: '0.25rem', 
                  //             fontSize: 'clamp(0.65rem, 1vw, 0.75rem)',
                  //             fontWeight: 600,
                  //             backgroundColor: '#6f42c1',
                  //             color: '#fff'
                  //           }}>
                  //             JobCreations Task
                  //           </span>
                  //         </div>
                  //       )}
                        
                  //       {/* Process Details */}
                  //       {shouldShowProcess && (
                  //         <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: '#1f2937' }}>
                  //           Process: {processValue}{task.stepNo ? ` - Step ${task.stepNo}` : ''}
                  //         </div>
                  //       )}
                        
                  //       {/* Description */}
                  //       {shouldShowDescription && (
                  //         <div style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', color: '#6c757d', marginBottom: '0.25rem' }}>
                  //           Description: {descriptionValue}
                  //         </div>
                  //       )}
                        
                  //       {/* Step Status */}
                  //       {task.stepStatus && (
                  //         <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)', marginBottom: '0.25rem' }}>
                  //           <span style={{ 
                  //             padding: '0.2em 0.5em', 
                  //             borderRadius: '0.25rem', 
                  //             fontSize: 'clamp(0.65rem, 1vw, 0.75rem)',
                  //             fontWeight: 600,
                  //             backgroundColor: task.stepStatus === 'Completed' ? '#28a745' :
                  //                           task.stepStatus === 'In Progress' ? '#ffc107' :
                  //                           task.stepStatus === 'Pending' ? '#dc3545' : '#6c757d',
                  //             color: task.stepStatus === 'In Progress' ? '#212529' : '#fff'
                  //           }}>
                  //             Status: {task.stepStatus}
                  //           </span>
                  //         </div>
                  //       )}
                        
                  //       {/* Assignment Details - Assigned To from JobCreations */}
                  //       {task.assignedTo && (
                  //         <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)', color: '#007bff', marginBottom: '0.25rem', fontWeight: 600 }}>
                  //           Assigned To: {task.assignedTo}
                  //           {task.assignedId > 0 && (
                  //             <span style={{ fontSize: 'clamp(0.625rem, 1vw, 0.7rem)', color: '#6c757d', marginLeft: '0.5rem' }}>
                  //               (ID: {task.assignedId})
                  //             </span>
                  //           )}
                  //           {task.workstationId > 0 && (
                  //             <span style={{ fontSize: 'clamp(0.625rem, 1vw, 0.7rem)', color: '#6c757d', marginLeft: '0.5rem' }}>
                  //               (Workstation: {task.workstationName || task.workstationId})
                  //             </span>
                  //           )}
                  //         </div>
                  //       )}
                        
                  //       {/* Date Information */}
                  //       <div style={{ fontSize: 'clamp(0.65rem, 1.2vw, 0.75rem)', color: '#6c757d' }}>
                  //         {task.startTime && task.startTime !== 'Invalid date' && (
                  //           <div style={{ marginBottom: '0.1rem' }}>
                  //             Start Time: {new Date(task.startTime).toLocaleDateString()}
                  //           </div>
                  //         )}
                  //         {task.endTime && task.endTime !== 'Invalid date' && (
                  //           <div style={{ marginBottom: '0.1rem' }}>
                  //             End Time: {new Date(task.endTime).toLocaleDateString()}
                  //           </div>
                  //         )}
                  //       </div>
                  //     </div>
                  //     <div style={styles.taskDate}>
                  //       {task.startDate || task.endDate || task.dueDate ? (
                  //         <div style={{ fontSize: 'clamp(0.65rem, 1.2vw, 0.75rem)', color: '#6c757d', marginBottom: '0.25rem' }}>
                  //           {task.startDate && new Date(task.startDate).toDateString() !== new Date().toDateString() && (
                  //             <div>Start: {new Date(task.startDate).toLocaleDateString()}</div>
                  //           )}
                  //           {task.endDate && new Date(task.endDate).toDateString() !== new Date().toDateString() && (
                  //             <div>End: {new Date(task.endDate).toLocaleDateString()}</div>
                  //           )}
                  //         </div>
                  //       ) : null}
                  //       {task.dueDate && (
                  //         (() => {
                  //           const dueDate = new Date(task.dueDate);
                  //           const today = new Date();
                  //           today.setHours(0, 0, 0, 0);
                  //           dueDate.setHours(0, 0, 0, 0);
                  //           const isOverdue = dueDate < today;
                  //           const isDueToday = dueDate.getTime() === today.getTime();
                            
                  //           return (
                  //             <div style={{ 
                  //               fontSize: 'clamp(0.65rem, 1.2vw, 0.75rem)', 
                  //               color: isOverdue ? '#dc3545' : (isDueToday ? '#ffc107' : '#6c757d'),
                  //               marginTop: '0.25rem',
                  //               fontWeight: isOverdue || isDueToday ? 600 : 400
                  //             }}>
                  //               <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  //                 <span>Due Date:</span>
                  //                 <span>{dueDate.toLocaleDateString()}</span>
                  //                 {isOverdue && (
                  //                   <span style={{ 
                  //                     fontSize: 'clamp(0.6rem, 1vw, 0.7rem)',
                  //                     backgroundColor: '#dc3545',
                  //                     color: '#fff',
                  //                     padding: '0.1em 0.4em',
                  //                     borderRadius: '0.25rem',
                  //                     fontWeight: 600
                  //                   }}>
                  //                     OVERDUE
                  //                   </span>
                  //                 )}
                  //                 {isDueToday && !isOverdue && (
                  //                   <span style={{ 
                  //                     fontSize: 'clamp(0.6rem, 1vw, 0.7rem)',
                  //                     backgroundColor: '#ffc107',
                  //                     color: '#212529',
                  //                     padding: '0.1em 0.4em',
                  //                     borderRadius: '0.25rem',
                  //                     fontWeight: 600
                  //                   }}>
                  //                     DUE TODAY
                  //                   </span>
                  //                 )}
                  //         </div>
                  //             </div>
                  //           );
                  //         })()
                  //       )}
                  //     </div>
                  //   </div>
                  //   <div style={{ ...styles.taskStatus, backgroundColor: getStatusColor(task.status) }}></div>
                  // </div>
                // );
              
              return(
               <div key={task.id} style={styles.taskItem}>
                    <div style={styles.taskContent}>
                      <div style={styles.taskText}>
                        <div className="mb-3" style={{ fontWeight: 600, marginBottom: '0.25rem', color: '#1f2937' }}>
                          You have been assigned the following task
                        </div>
                        
                       
                       {task.processname && (
                          <div style={{ fontWeight: 400, marginBottom: '0.25rem', color: '#1f2937' }}>
                            Step Name: {task.processname}
                          </div>
                        )}
                        
                        {/* {task.jdescription && (
                          <div style={{ fontWeight: 400, marginBottom: '0.25rem', color: '#1f2937' }}>
                            Step Description: {task.jdescription}
                          </div>
                        )} */}

                         {task.jobNo && (
                          <div style={{ fontWeight: 400, marginBottom: '0.25rem', color: '#1f2937' }}>
                            Job Order No:
                            <span className="ms-2" style={{ 
                                      fontSize: 'clamp(0.6rem, 1vw, 0.7rem)',
                                      backgroundColor: '#5435dcff',
                                      color: '#fff',
                                      padding: '0.1em 0.4em',
                                      borderRadius: '0.25rem',
                                      fontWeight: 600
                                    }} onClick={() => handleJobClick(task)}> {task.jobNo}</span>
                          </div>
                        )}
                
                        
                        {/* Assignment Details - Assigned To from JobCreations */}
                        {(task.workstationid > 0 || task.assignedid > 0) && (
                        <div style={{ fontWeight: 400, marginBottom: '0.25rem', color: '#1f2937' }}>
                          {task.workstationid > 0
                            ? `Workstation: ${task.workStationName}`
                            : task.assignedid > 0
                            ? `Assigned: ${task.username}`
                            : null
                          }
                        </div>
                      )}

                        {/* Date Information */}
                          <div>
                        {task.dueDate && (
                          (() => {
                            const dueDate = new Date(task.dueDate);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            dueDate.setHours(0, 0, 0, 0);
                            const isOverdue = dueDate < today;
                            const isDueToday = dueDate.getTime() === today.getTime();
                            
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                 <div style={{ fontWeight: 400, marginBottom: '0.25rem', color: '#1f2937' }}>
                                  Due Date: {dueDate.toLocaleDateString()}
                                  </div>
                                  {isOverdue && (
                                    <span style={{ 
                                      fontSize: 'clamp(0.6rem, 1vw, 0.7rem)',
                                      backgroundColor: '#dc3545',
                                      color: '#fff',
                                      padding: '0.1em 0.4em',
                                      borderRadius: '0.25rem',
                                      fontWeight: 600
                                    }}>
                                      OVERDUE
                                    </span>
                                  )}
                                  {isDueToday && !isOverdue && (
                                    <span style={{ 
                                      fontSize: 'clamp(0.6rem, 1vw, 0.7rem)',
                                      backgroundColor: '#ffc107',
                                      color: '#212529',
                                      padding: '0.1em 0.4em',
                                      borderRadius: '0.25rem',
                                      fontWeight: 600
                                    }}>
                                      DUE TODAY
                                    </span>
                                  )}
                              </div>
                            );
                          })()
                        )}
                      </div>
                      </div>
                      <div className='mt-2'>
                     {task.assignedBy && (
                          <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)', color: '#495057', marginBottom: '0.2rem' }}>
                            Assigned By: {task.assignedBy}
                          </div>
                        )}

                  {task.orderDate && (
                          (() => {
                            const dueDate = new Date(task.orderDate);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            dueDate.setHours(0, 0, 0, 0);
                            
                            return (
                        <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)', color: '#495057', marginBottom: '0.2rem' }}>                                
                        {dueDate.toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                         </div>
                            );
                          })()
                        )}
                        </div>
                    </div>
                  </div>
              );
              })
              ) : (
                <div style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}>
                  {taskSearchTerm ? `No tasks found matching "${taskSearchTerm}"` : 'No assigned tasks found.'}
                </div>
              )}
              
            </div>
          </div>

          {/* Jobs */}
          <div style={styles.card} className="dashboard-card">
            <h3 style={styles.cardTitle}>Jobs</h3>
             <input 
              type="text" 
              placeholder="Search by JO#..." 
              style={styles.searchInput}
              value={jobSearchTerm}
              onChange={(e) => setJobSearchTerm(e.target.value)}
            />
            <div>
              {sectionLoading.jobs ? JobsListSkeleton : filteredJobs.length > 0 ? (
              filteredJobs.map((job, index) => (
                <div key={index} style={styles.jobItem}>
                  <div style={styles.jobHeader}>
                    <span style={styles.jobId}>{job.id}</span>
                    <span style={{ ...styles.jobStatus, backgroundColor: getStatusColor(job.status) }}>
                      {job.status}
                    </span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${job.progress}%` }}></div>
                  </div>
                  <div style={styles.jobMetrics}>
                    <span>{job.complete} Complete</span>
                    <span>{job.total} Total Steps</span>
                    <span>{job.daysDue} Days Due</span>
                  </div>
                </div>
              ))
               ) : (
                <div style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}>
                  {jobSearchTerm ? `No jobs found matching "${jobSearchTerm}"` : 'No assigned jobs found.'}
                </div>
              )}
                  {jobs.length > 10 && (
          <div style={{textAlign: 'center', marginTop: '1rem'}}>
            <button
              style={styles.viewAllButton}
              onClick={() => setShowAllJobs(!showAllJobs)}
            >
              {showAllJobs ? 'Show Less' : 'View All Jobs'}
              {showAllJobs ? (
                <FaChevronUp style={{ marginLeft: 4, fontSize: '0.7rem' }} />
              ) : (
                <FaChevronDown style={{ marginLeft: 4, fontSize: '0.7rem' }} />
              )}
            </button>
          </div>
        )}
              </div>
          </div>

          {/* Timeline Mini */}
          <div style={styles.card} className="dashboard-card">
            <h3 style={styles.cardTitle}>Messages</h3>
            <div>
              {sectionLoading.messages ? MessagesListSkeleton : timeline && timeline.length > 0 ? (
                timeline.map((item: any, index: number) => (
                  <div key={index} style={styles.timelineItem}>
                    {getTimelineIcon(item.type)}
                    <div style={styles.timelineContent}>
                      <div style={styles.timelineHeader}>
                        <span style={{ ...styles.timelineBadge, backgroundColor: 
                          item.type === 'alert' ? '#dc3545' : 
                          item.type === 'message' ? '#007bff' : '#ffc107'
                        }}>
                          {item.type === 'alert' && <span>✕</span>}
                          {item.type === 'warning' && <span>?</span>}
                          {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                        </span>
                        <span style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.75rem)', color: '#6c757d' }}>{item.timeAgo}</span>
                        <span>{item.date && <div style={styles.timelineDate}>{item.date}</div>}</span>
                      </div>
                      <div style={styles.timelineText}>{item.content.replace(/\s*@\S+/g, '')}</div>
                     
                      {(() => {
                        const referenceLabel = item.referenceLabel ||  labelMap[item.entryType] || item.entryType;
                        const referenceValue = item.referenceValue || item.uniqueNo;
                        const msgValue = item.msgnumber;
                        if (!referenceLabel || !referenceValue) {
                          return null;
                        }
                        return (
                          <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.75rem)', color: '#6c757d', marginTop: '0.25rem' }}
                          
                          >
                            {referenceLabel}: <span className="ms-2" style={{ 
                                      fontSize: 'clamp(0.6rem, 1vw, 0.7rem)',
                                      backgroundColor: '#5435dcff',
                                      color: '#fff',
                                      padding: '0.1em 0.4em',
                                      borderRadius: '0.25rem',
                                      fontWeight: 600
                                    }} onClick={() => handleReferenceClick(item)}>{msgValue}</span>
                          </div>
                        );
                      })()}
                       {item.author && (
                        <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.75rem)', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ color: '#007bff' }}>By: {item.author}</span>
                          {/* {item.messageType && (
                            <span style={{ 
                              fontSize: 'clamp(0.65rem, 1vw, 0.7rem)',
                              padding: '0.15em 0.5em',
                              borderRadius: '0.25rem',
                              fontWeight: 600,
                              backgroundColor: item.messageType === 'sent' ? '#28a745' : '#007bff',
                              color: '#fff'
                            }}>
                              {item.messageType === 'sent' ? 'Sent' : 'Received'}
                            </span>
                          )} */}
                        </div>
                      )}
                      {item.readed === false && (
                        <div style={{ fontSize: 'clamp(0.7rem, 1.2vw, 0.75rem)', color: '#dc3545', marginTop: '0.25rem' }}>
                          ● Unread
                        </div>
                      )}
                      
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}>
                  No recent comments available
                </div>
              )}
            </div>
          </div>

          
        </div>
         {/* Slideout panels — lazy-loaded components require a Suspense boundary */}
         <Suspense fallback={null}>
         {slideout.open && slideout.type === "JobOrderTask" && (
                <JobCreations
                  closeSlideOut={closeSlideout}
                  jobid={slideout.data.jobId }
                  orderid={slideout.data.orderId }
                  productid={slideout.data.partId }
                  id={slideout.data.orderId} 
                />
              )}

            {slideout.open && slideout.type === "Quotation" && (
                              <Quotationslideout
                                closeSlideOut={closeSlideout}
                                orderid={slideout.data.uniqueNo}>
                              </Quotationslideout>
             )}

              {slideout.open && slideout.type === "CustomerOrder" && (
                               <Customerorderslideout
                                 closeSlideOut={closeSlideout}
                                 orderid={slideout.data.uniqueNo}
                               >
                               </Customerorderslideout>
                             )}

              {slideout.open && slideout.type === "deposit" && (
                               <DepositSlideout
                                 closeSlideOut={closeSlideout}
                                 transactionID={slideout.data.uniqueNo}
                               >
                               </DepositSlideout>
                             )}

               {slideout.open && slideout.type === "withdrawal" && (
                               <WithdrawalSlideout
                                 closeSlideOut={closeSlideout}
                                 transactionID={slideout.data.uniqueNo}
                               >
                               </WithdrawalSlideout>
                             )}

                 {slideout.open && slideout.type === "accountsInvoice" && (
                               <InvoiceSlideout
                                 closeSlideOut={closeSlideout}
                                 transactionID={slideout.data.uniqueNo}
                               >
                               </InvoiceSlideout>
                             )}
              
               {slideout.open && slideout.type === "CustomerInvoice" && (
                               <Receivableslideout
                                 closeSlideOut={closeSlideout}
                                 invoiceId={slideout.data.uniqueNo}
                               >
                               </Receivableslideout>
                             )}

                  {slideout.open && slideout.type === "vendorInvoice" && (
                               <InvoiceEntryslideout
                                 closeSlideOut={closeSlideout}
                                 invoiceId={slideout.data.uniqueNo}
                               >
                               </InvoiceEntryslideout>
                             )}

                  {slideout.open && slideout.type === "JobOrder" && (
                <JobCreations
                  closeSlideOut={closeSlideout}
                  jobid={slideout.data.uniqueNo }
                  orderid={slideout.data.orderid }
                  productid={slideout.data.partid }
                  id={slideout.data.orderid} 
                />
              )}
         </Suspense>

             
      </div>
    </div>
    </>
  );
};

export default AirframeDashboard;
